import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { logout as logoutAction } from '@/store/slices/authSlice';
import { tokenService } from '@/utils/tokenService';
import { IoLogOutOutline, IoHomeSharp } from 'react-icons/io5';
import { useLogoutMutation } from '@/store/api/authApi';
import { FaUser } from 'react-icons/fa';
import { TbPlayCardStarFilled } from 'react-icons/tb';
import { useDisconnect } from '@reown/appkit/react';
import { HiMiniSignal } from 'react-icons/hi2';
import { MdDashboard, MdHowToVote } from 'react-icons/md';

type MenuItemsProps = {
  isMobile?: boolean;
  onNavigate?: () => void;
};

export const MenuItems = ({ isMobile = false, onNavigate }: MenuItemsProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [logout] = useLogoutMutation();
  const dispatch = useDispatch();
  const { disconnect } = useDisconnect();

  const handleLogout = async () => {
    try {
      await logout().unwrap();
    } catch (err) {
      console.error('Logout failed:', err);
    } finally {
      tokenService.clear();

      document.cookie.split(';').forEach((c) => {
        document.cookie = c
          .replace(/^ +/, '')
          .replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
      });

      dispatch(logoutAction());
      onNavigate?.();
      disconnect();
      window.location.href = '/';
    }
  };

  const isActive = (path: string) => location.pathname.startsWith(path);

  const handleNav = (path?: string) => (e: React.MouseEvent) => {
    if (!path) return;
    e.preventDefault();
    navigate(path);
    onNavigate?.();
  };

  return (
    <div className="flex flex-col">
      <DropdownItem
        icon={<IoHomeSharp />}
        label="Home"
        to="/"
        isActive={location.pathname === '/'}
        isMobile={isMobile}
        onNavigate={handleNav('/')}
      />

      <DropdownItem
        icon={<MdDashboard />}
        label="Dashboard"
        to="/dashboard"
        isActive={isActive('/dashboard')}
        isMobile={isMobile}
        onNavigate={handleNav('/dashboard')}
      />
      <DropdownItem
        icon={<FaUser />}
        label="Profile"
        to="/Profile"
        isActive={isActive('/Profile')}
        isMobile={isMobile}
        onNavigate={handleNav('/Profile')}
      />

      <DropdownItem
        icon={<HiMiniSignal />}
        label="Live"
        to="/live"
        isActive={isActive('/live')}
        isMobile={isMobile}
        onNavigate={handleNav('/live')}
      />

      <DropdownItem
        icon={<MdHowToVote />}
        label="Votes"
        to="/votes"
        isActive={isActive('/votes')}
        isMobile={isMobile}
        onNavigate={handleNav('/votes')}
      />
      <DropdownItem
        icon={<TbPlayCardStarFilled />}
        label="Collectibles"
        to="/nft-cards"
        isActive={isActive('/nft-cards')}
        isMobile={isMobile}
        onNavigate={handleNav('/nft-cards')}
      />

      {/* <DropdownItem icon={<IoSettingsOutline />} label="Settings" isMobile={isMobile} /> */}

      <button
        type="button"
        className={`flex items-center w-full gap-3 sm:gap-4 text-left text-red-500 hover:bg-white/5 mt-2 ${
          isMobile ? 'px-4 sm:px-6 py-3 sm:py-4 border-t border-[#131F6D]' : 'px-6 py-3'
        }`}
        onClick={handleLogout}
      >
        <IoLogOutOutline size={isMobile ? 24 : 20} />
        <span className={`${isMobile ? 'text-[15px] sm:text-[16px]' : 'text-base'} font-semibold`}>
          Logout
        </span>
      </button>
    </div>
  );
};

const DropdownItem = ({
  icon,
  label,
  isMobile,
  to,
  onNavigate,
  isActive = false,
}: {
  icon: React.ReactNode;
  label: string;
  isMobile?: boolean;
  to?: string;
  onNavigate?: (e: React.MouseEvent) => void;
  isActive?: boolean;
}) => (
  <NavLink
    to={to ?? '#'}
    onClick={onNavigate}
    className={`flex items-center w-full gap-3 sm:gap-4 text-left transition-colors border-y border-[#131F6D] ${
      isMobile ? 'px-4 sm:px-6 py-3 sm:py-4' : 'px-6 py-3'
    } ${isActive ? 'bg-[#2C3FC2] text-white' : 'bg-[#000001] text-white hover:bg-[#2C3FC2]'}`}
  >
    <div className={`${isMobile ? 'text-[20px] sm:text-[24px]' : 'text-[20px]'}`}>{icon}</div>
    <span className={`${isMobile ? 'text-[15px] sm:text-[16px] font-semibold' : 'text-base'}`}>
      {label}
    </span>
  </NavLink>
);
